// useEditorKeyboardShortcuts.js — Shared keyboard shortcut handler for full-screen editor modals.
//
// Wires Ctrl+Z / Ctrl+Y / Ctrl+Shift+Z (undo/redo), Delete/Backspace (delete selection),
// and an optional map of single-character shortcuts for editor-specific tool modes.
//
// ponytail: extraShortcuts is evaluated on every keydown but the map reference is
//   stable (callers memoize it), so no re-registration overhead.

import {useEffect} from 'react';

/**
 * @param {React.RefObject} containerRef   - element that receives focus (tabIndex={-1})
 * @param {object}          opts
 * @param {() => void}      opts.onUndo
 * @param {() => void}      opts.onRedo
 * @param {() => void}      opts.onDelete
 * @param {Record<string, () => void>} [opts.extraShortcuts]
 *   Single-key shortcuts fired when no modifier is held and the target is not an
 *   input/textarea. Keys should be lowercase (compared via e.key.toLowerCase()).
 * @param {() => boolean}  [opts.guardIsEditing]
 *   Return true to suppress all shortcuts (e.g. when a Fabric text object is in edit mode).
 */
function useEditorKeyboardShortcuts(containerRef, {onUndo, onRedo, onDelete, extraShortcuts, guardIsEditing} = {}) {
    useEffect(() => {
        const el = containerRef?.current;
        if (!el) return;

        const onKeyDown = (e) => {
            if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
            if (e.target.closest?.('[data-no-shortcuts]')) return;
            const role = e.target.getAttribute?.('role');
            if (role === 'combobox' || role === 'option' || role === 'listbox') return;
            if (e.target.getAttribute?.('contenteditable') === 'true') return;
            if (guardIsEditing?.()) return;

            if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
                e.preventDefault();
                onUndo?.();
                return;
            }
            if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.shiftKey && e.key === 'z'))) {
                e.preventDefault();
                onRedo?.();
                return;
            }
            if ((e.key === 'Delete' || e.key === 'Backspace') && !e.ctrlKey && !e.metaKey) {
                e.preventDefault();
                onDelete?.();
                return;
            }

            if (extraShortcuts && !e.ctrlKey && !e.metaKey && !e.altKey && e.key !== ' ') {
                const handler = extraShortcuts[e.key.toLowerCase()];
                if (handler) handler();
            }
        };

        el.addEventListener('keydown', onKeyDown);
        return () => el.removeEventListener('keydown', onKeyDown);
    // Re-register only when the container element or stable callbacks change.
    // extraShortcuts and guardIsEditing are expected to be stable references.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [containerRef, onUndo, onRedo, onDelete]);
}

export default useEditorKeyboardShortcuts;
